// src/pages/CapturePage/PreflightCard.js
//
// Device, microphone, distance and room noise — before anything is recorded.
//
// Most of it fills itself: the browser knows the device and the microphone, and
// three seconds of listening gives the room. What is left is the one thing no
// sensor can tell us — how far the microphone is from the child's mouth — and a
// confirmation that the room is workable.
import React, { useEffect, useRef, useState } from 'react';
import { measureRoomNoise } from './noiseMeter';
import { captureError, savePreflight } from './captureApi';

function describeDevice() {
    const ua = navigator.userAgent || '';
    const isTablet = /iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua));
    const isPhone = /iPhone|Android.*Mobile|Windows Phone/i.test(ua);
    const browser = /Edg\//.test(ua) ? 'Edge'
        : /OPR\//.test(ua) ? 'Opera'
            : /Chrome\//.test(ua) ? 'Chrome'
                : /Safari\//.test(ua) ? 'Safari'
                    : /Firefox\//.test(ua) ? 'Firefox' : 'Unknown';
    return {
        device_type: isTablet ? 'tablet' : isPhone ? 'phone' : 'laptop',
        device_model: (navigator.platform || 'unknown').slice(0, 120),
        browser: `${browser} · ${ua.slice(0, 80)}`,
    };
}

export default function PreflightCard({ session, protocol, onSaved }) {
    const bands = protocol?.preflight?.noise_bands || [];
    const bandLabel = (key) => (bands.find((b) => b.key === key) || {}).en || key;

    const [distance, setDistance] = useState(
        session.mic_distance_cm || protocol?.preflight?.default_distance_cm || 20
    );
    const [band, setBand] = useState(session.noise_band || '');
    const [measuring, setMeasuring] = useState(false);
    const [level, setLevel] = useState(null);
    const [measured, setMeasured] = useState(
        session.noise_dbfs != null ? { dbfs: session.noise_dbfs } : null
    );
    const [quiet, setQuiet] = useState(session.quiet_room === true);
    const [issues, setIssues] = useState(session.equipment_issues || '');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const sentDeviceRef = useRef(false);

    // The device details need no one's attention: record them once and move on.
    useEffect(() => {
        if (sentDeviceRef.current || session.device_type) return;
        sentDeviceRef.current = true;
        savePreflight(session.id, describeDevice()).then((r) => onSaved(r.data)).catch(() => {
            // Not worth interrupting anyone: the examiner's own entries still save.
        });
    }, [session.id, session.device_type, onSaved]);

    const listen = async () => {
        setError('');
        setMeasuring(true);
        setLevel(null);
        try {
            const seconds = protocol?.preflight?.measure_seconds || 3;
            const result = await measureRoomNoise({ seconds, onLevel: setLevel });
            setMeasured(result);
            const { bandFor } = await import('./noiseMeter');
            const suggested = bandFor(result.dbfs, bands);
            setBand(suggested);
            const saved = await savePreflight(session.id, {
                noise_dbfs: result.dbfs, noise_band: suggested,
            });
            onSaved(saved.data);
        } catch (e) {
            setError(
                e?.name === 'NotAllowedError'
                    ? 'The browser blocked the microphone. Allow it, then measure again.'
                    : 'Could not measure the room. Set the level yourself below.'
            );
        } finally {
            setMeasuring(false);
            setLevel(null);
        }
    };

    const save = async (fields) => {
        setError('');
        setSaving(true);
        try {
            const r = await savePreflight(session.id, fields);
            onSaved(r.data);
        } catch (e) {
            setError(captureError(e, 'Could not save. Check the connection and try again.'));
        } finally {
            setSaving(false);
        }
    };

    const chooseBand = (key) => { setBand(key); save({ noise_band: key }); };
    const commitDistance = () => save({ mic_distance_cm: Number(distance) });
    const toggleQuiet = () => { const next = !quiet; setQuiet(next); save({ quiet_room: next }); };

    return (
        <div className="cap-card cap-preflight">
            <h3>Before you record</h3>
            <p className="cap-hint">
                A rate or a voice measurement is only meaningful if we know how far away the
                microphone was and how loud the room is. Recording stays closed until this is filled in.
            </p>

            <div className="cap-field">
                <label>Device</label>
                <div className="cap-auto">
                    {session.device_type || '—'}
                    {session.device_model ? ` · ${session.device_model}` : ''}
                    <span className="cap-tag">filled in automatically</span>
                </div>
            </div>

            <div className="cap-field">
                <label htmlFor="cap-distance">Microphone distance from the child's mouth</label>
                <div className="cap-distance">
                    <input
                        id="cap-distance" type="range" min="5" max="60" step="5"
                        value={distance}
                        onChange={(e) => setDistance(e.target.value)}
                        onMouseUp={commitDistance}
                        onTouchEnd={commitDistance}
                        onBlur={commitDistance}
                    />
                    <strong>{distance} cm</strong>
                </div>
                <p className="cap-hint">About a hand's width is 20 cm. Keep it the same for every block.</p>
            </div>

            <div className="cap-field">
                <label>Room noise</label>
                <button type="button" className="cap-btn ghost" onClick={listen} disabled={measuring}>
                    {measuring ? 'Listening…' : measured ? 'Measure again' : 'Listen to the room (3 s)'}
                </button>
                {measuring && (
                    <div className="cap-meter" aria-hidden="true">
                        <span style={{ width: `${Math.max(0, Math.min(100, (level ?? -100) + 100))}%` }} />
                    </div>
                )}
                {measured && (
                    <p className="cap-measured">
                        Measured {measured.dbfs} dBFS — suggests <strong>{bandLabel(band)}</strong>.
                        You can change it if you know better.
                    </p>
                )}
                <div className="cap-choices">
                    {bands.map((b) => (
                        <button
                            key={b.key} type="button"
                            className={`cap-choice${band === b.key ? ' selected' : ''}`}
                            onClick={() => chooseBand(b.key)}
                        >
                            {b.en}
                            {b.hi ? <em>{b.hi}</em> : null}
                        </button>
                    ))}
                </div>
            </div>

            <label className="cap-check">
                <input type="checkbox" checked={quiet} onChange={toggleQuiet} />
                <span>
                    The room is workable: door shut, no fan or television directly on the microphone,
                    nobody else talking.
                </span>
            </label>

            <div className="cap-field">
                <label htmlFor="cap-issues">Anything wrong with the equipment? (optional)</label>
                <textarea
                    id="cap-issues" rows="2" value={issues}
                    onChange={(e) => setIssues(e.target.value)}
                    onBlur={() => save({ equipment_issues: issues })}
                    placeholder="Headset cable loose, tablet microphone muffled…"
                />
            </div>

            {error ? <p className="cap-error">{error}</p> : null}
            {saving ? <p className="cap-hint">Saving…</p> : null}

            {session.preflight_complete ? (
                <p className="cap-ok">Pre-flight complete — the blocks are open.</p>
            ) : (
                <p className="cap-hint">
                    Still needed: {(session.preflight_missing || []).map((f) => ({
                        mic_distance_cm: 'microphone distance',
                        noise_band: 'room noise',
                        quiet_room: 'quiet-room confirmation',
                    }[f] || f)).join(', ')}.
                </p>
            )}
        </div>
    );
}
